<script setup lang="ts">
import useOnboardingStore from '@/store/modules/onboarding'
import useUserStore from '@/store/modules/user'
import { useI18n } from 'vue-i18n'

interface LauncherDragState {
  pointerId: number
  startX: number
  startY: number
  left: number
  top: number
  moved: boolean
}

const onboardingStore = useOnboardingStore()
const userStore = useUserStore()
const { t } = useI18n()
const shellRef = ref<HTMLElement | null>(null)
const positionStyle = ref<Record<string, string>>({})
const dragging = ref(false)
let dragState: LauncherDragState | null = null
let suppressOpenUntil = 0

const completedTaskCount = computed(() => onboardingStore.tasks
  .filter(task => onboardingStore.completedTaskIds.includes(task.id))
  .length)
const totalTaskCount = computed(() => onboardingStore.tasks.length)

const visible = computed(() => (
  userStore.isLogin
  // 等 GET 进度完成后再开放手动入口，避免用户在 revision/exists 尚未确定时提前操作。
  && onboardingStore.remoteStateLoaded
  && onboardingStore.status !== 'running'
  && !onboardingStore.welcomeVisible
  && !onboardingStore.taskCenterVisible
  && !onboardingStore.launcherDismissed
))

function openOnboarding() {
  // 手动打开教程时由 Store 统一决定：
  // 已有后端 cursor 则恢复到 GET 的 taskId/stepId；没有初始化则打开欢迎页。
  onboardingStore.openManually()
}

function clamp(value: number, minimum: number, maximum: number) {
  return Math.max(minimum, Math.min(maximum, value))
}

async function applyLauncherPosition() {
  await nextTick()
  const shell = shellRef.value
  if (!shell || !visible.value) {
    return
  }
  const width = Math.max(1, shell.offsetWidth)
  const height = Math.max(1, shell.offsetHeight)
  const inset = window.innerWidth <= 767 ? 14 : 22
  const maxLeft = Math.max(8, window.innerWidth - width - 8)
  const maxTop = Math.max(8, window.innerHeight - height - 8)
  const { edge, yRatio } = onboardingStore.launcherPosition
  const left = edge === 'left' ? inset : window.innerWidth - width - inset
  const top = yRatio === null
    ? window.innerHeight - height - inset
    : 8 + yRatio * Math.max(0, maxTop - 8)
  positionStyle.value = {
    left: `${clamp(left, 8, maxLeft)}px`,
    top: `${clamp(top, 8, maxTop)}px`,
    right: 'auto',
    bottom: 'auto',
  }
}

function handlePointerDown(event: PointerEvent) {
  if (event.button !== 0 || !shellRef.value) {
    return
  }
  const rect = shellRef.value.getBoundingClientRect()
  dragState = {
    pointerId: event.pointerId,
    startX: event.clientX,
    startY: event.clientY,
    left: rect.left,
    top: rect.top,
    moved: false,
  }
  shellRef.value.setPointerCapture?.(event.pointerId)
}

function handlePointerMove(event: PointerEvent) {
  const shell = shellRef.value
  if (!shell || !dragState || dragState.pointerId !== event.pointerId) {
    return
  }
  const deltaX = event.clientX - dragState.startX
  const deltaY = event.clientY - dragState.startY
  if (!dragState.moved && Math.hypot(deltaX, deltaY) < 5) {
    return
  }
  dragState.moved = true
  dragging.value = true
  event.preventDefault()
  positionStyle.value = {
    left: `${clamp(dragState.left + deltaX, 8, window.innerWidth - shell.offsetWidth - 8)}px`,
    top: `${clamp(dragState.top + deltaY, 8, window.innerHeight - shell.offsetHeight - 8)}px`,
    right: 'auto',
    bottom: 'auto',
  }
}

function finishDrag(event: PointerEvent) {
  const shell = shellRef.value
  if (!shell || !dragState || dragState.pointerId !== event.pointerId) {
    return
  }
  const moved = dragState.moved
  dragState = null
  shell.releasePointerCapture?.(event.pointerId)
  dragging.value = false
  if (!moved) {
    // Pointer capture can prevent Element Plus from emitting a reliable click.
    // Open the task center from pointerup, then suppress the synthetic click.
    suppressOpenUntil = Date.now() + 350
    openOnboarding()
    return
  }
  suppressOpenUntil = Date.now() + 350
  const rect = shell.getBoundingClientRect()
  const edge = rect.left + rect.width / 2 < window.innerWidth / 2 ? 'left' : 'right'
  const availableHeight = Math.max(1, window.innerHeight - rect.height - 16)
  onboardingStore.setLauncherPosition(
    edge,
    clamp((rect.top - 8) / availableHeight, 0, 1),
  )
  applyLauncherPosition()
}

function handleOpen() {
  if (Date.now() < suppressOpenUntil) {
    return
  }
  openOnboarding()
}

function handleDismissLauncher() {
  onboardingStore.dismissLauncher()
}

watch(
  () => [visible.value, onboardingStore.launcherPosition.edge, onboardingStore.launcherPosition.yRatio],
  () => applyLauncherPosition(),
  { immediate: true },
)

onMounted(() => {
  window.addEventListener('resize', applyLauncherPosition)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', applyLauncherPosition)
})
</script>

<template>
  <div
    v-if="visible"
    ref="shellRef"
    class="onboarding-launcher-shell"
    :class="{ 'is-dragging': dragging }"
    :style="positionStyle"
    @pointerdown="handlePointerDown"
    @pointermove="handlePointerMove"
    @pointerup="finishDrag"
    @pointercancel="finishDrag"
  >
    <button
      class="onboarding-launcher-dismiss"
      type="button"
      :aria-label="t('onboarding.common.dismissLauncher')"
      :title="t('onboarding.common.dismissLauncher')"
      @pointerdown.stop
      @pointermove.stop
      @pointerup.stop
      @pointercancel.stop
      @click.stop="handleDismissLauncher"
    >
      ×
    </button>
    <el-button
      class="onboarding-task-launcher"
      type="primary"
      :aria-label="t('onboarding.common.openCenter')"
      @click.stop="handleOpen"
    >
      <span>{{ t('onboarding.common.launcherProgress', {
        completed: completedTaskCount,
        total: totalTaskCount,
      }) }}</span>
    </el-button>
  </div>
</template>

<style scoped>
.onboarding-launcher-shell {
  position: fixed;
  right: max(22px, calc(env(safe-area-inset-right, 0px) + 14px));
  bottom: max(22px, calc(env(safe-area-inset-bottom, 0px) + 14px));
  z-index: 33000;
  touch-action: none;
  user-select: none;
}

.onboarding-launcher-dismiss {
  position: absolute;
  top: -8px;
  right: -8px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;

  font-size: 16px;
  line-height: 1;
  color: var(--el-text-color-regular);
  cursor: pointer;
  background: var(--el-bg-color);
  border: 1px solid var(--el-border-color);
  border-radius: 50%;
  box-shadow: 0 4px 12px rgb(15 23 42 / 16%);
}

.onboarding-launcher-dismiss:hover,
.onboarding-launcher-dismiss:focus-visible {
  color: var(--el-color-danger);
  border-color: var(--el-color-danger-light-5);
  outline: none;
}

.onboarding-task-launcher {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  min-width: 44px;
  height: 44px;
  padding: 0 15px;
  margin: 0;

  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  color: #fff;
  cursor: grab;
  user-select: none;
  background: var(--el-color-primary);
  border: 1px solid var(--el-color-primary);
  border-radius: 999px;
  box-shadow: 0 12px 28px rgb(37 99 235 / 28%);
  transition: background-color 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.onboarding-task-launcher:hover,
.onboarding-task-launcher:focus-visible {
  color: #fff;
  background: var(--el-color-primary-dark-2);
  border-color: var(--el-color-primary-dark-2);
}

.onboarding-launcher-shell.is-dragging .onboarding-task-launcher {
  cursor: grabbing;
  box-shadow: 0 14px 30px rgb(37 99 235 / 38%);
  transform: scale(1.02);
}

@media (width <= 767px) {
  .onboarding-launcher-shell {
    right: max(14px, env(safe-area-inset-right, 0px));
    bottom: max(14px, env(safe-area-inset-bottom, 0px));
  }
}

@media (prefers-reduced-motion: reduce) {
  .onboarding-task-launcher {
    transition: none;
  }
}
</style>
